import { InfoOutlined } from '@mui/icons-material';
import { Box, Grid, Skeleton, Stack, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Colors } from 'src/shared/components/styles/colors';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { getMemberStats } from 'src/shared/services/memberService';
import { ChartFailMessage } from 'src/modules/dashboard/components/atoms/chartFailMessage';
import { ColoredBox } from 'src/modules/dashboard/components/atoms/coloredBox';
import { HeaderSubTitle } from 'src/modules/dashboard/components/atoms/headerSubTitle';
import { LargeCounter } from 'src/modules/dashboard/components/atoms/largeCounter';
import { MemberStats } from 'src/modules/dashboard/models/memberStats';
import { MemberStatusTabKeysEnum } from 'src/shared/models/enums/memberStatusTabKeys.enum';
import { useDashboardFilter } from '../dashboardFilterContext';

export const MembersStatsWidget = () => {
  const { t } = useTranslation(['dashboard']);
  const isMounted = useIsMounted();
  const theme = useTheme();
  const isXS = useMediaQuery(theme.breakpoints.down('md'));
  const { associationId, districtId } = useDashboardFilter();

  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [stats, setStats] = useState<MemberStats>();

  // Synkad fontSize på siffrorna i de tre färgade korten — alla landar på den minsta storlek som
  // behövs för att längsta siffran ska få plats. Annars ser kortrucken otrevlig ut när "15 000"
  // tvingas till 13 px medan "36 st" ligger kvar på 24 px.
  const containerRef = useRef<HTMLDivElement | null>(null);
  const numberElsRef = useRef<Array<HTMLElement | null>>([null, null, null]);
  const [syncedFontPx, setSyncedFontPx] = useState<number | undefined>(undefined);

  const registerNumberEl = useCallback(
    (index: number) => (el: HTMLElement | null) => {
      numberElsRef.current[index] = el;
    },
    [],
  );

  useLayoutEffect(() => {
    if (isLoading || hasError) return undefined;
    const container = containerRef.current;
    if (!container) return undefined;

    const baseSizePx = isXS ? 16 : 24;
    const minSizePx = 13;

    const measure = () => {
      const els = numberElsRef.current.filter((el): el is HTMLElement => el !== null);
      if (els.length === 0) return;
      // Räkna ut hur bred texten *skulle* vara vid basstorleken utan att röra DOM:en — annars
      // hamnar vi i en feedback-loop (krympt → fits → reset → överflödar → krympt → ...).
      let smallestRatio = 1;
      els.forEach((el) => {
        const currentFontSize = parseFloat(window.getComputedStyle(el).fontSize);
        if (currentFontSize <= 0) return;
        const naturalNeeded = (el.scrollWidth * baseSizePx) / currentFontSize;
        const available = el.clientWidth;
        if (available > 0 && naturalNeeded > available) {
          const ratio = available / naturalNeeded;
          if (ratio < smallestRatio) smallestRatio = ratio;
        }
      });
      if (smallestRatio < 1) {
        setSyncedFontPx(Math.max(minSizePx, baseSizePx * smallestRatio));
      } else {
        setSyncedFontPx(undefined);
      }
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, [stats, isXS, isLoading, hasError]);

  useEffect(() => {
    setIsLoading(true);
    setHasError(false);
    const fetchData = async () => {
      try {
        const result = await getMemberStats(associationId, districtId);
        if (isMounted()) setStats(result.data);
      } catch (e) {
        if (isMounted()) {
          ConsoleHelper.error(e);
          setHasError(true);
        }
      } finally {
        if (isMounted()) setIsLoading(false);
      }
    };
    fetchData();
  }, [isMounted, associationId, districtId]);

  return (
    <Stack sx={{ p: 2, height: '100%' }} spacing={1.5}>
      <Stack spacing={0.25}>
        <Stack direction='row' alignItems='center' spacing={0.5}>
          <HeaderSubTitle label={t('dashboard:members.header')} />
          <Tooltip arrow title={t('dashboard:members.info')}>
            <InfoOutlined sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help', mb: 0.5 }} />
          </Tooltip>
        </Stack>
        <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>{t('dashboard:members.subtitle')}</Typography>
      </Stack>
      {isLoading && !stats ? (
        <Skeleton variant='text' sx={{ fontSize: (thm) => thm.typography.h3 }} />
      ) : (
        <LargeCounter count={stats?.nrOfMembers ?? 0} displaySuffix />
      )}
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
        {hasError ? (
          <ChartFailMessage />
        ) : (
          <Grid container spacing={1.5} ref={containerRef}>
            <ColoredBox
              label={t('dashboard:members.coloredBox.paid')}
              isLoading={isLoading}
              count={stats?.nrOfPaidMembers ?? 0}
              bgColor={Colors.ChipColors.Green}
              memberStatus={MemberStatusTabKeysEnum.Paid}
              numberRef={registerNumberEl(0)}
              numberFontSizePx={syncedFontPx}
            />
            <ColoredBox
              label={t('dashboard:members.coloredBox.unpaid')}
              isLoading={isLoading}
              count={stats?.nrOfUnPaidMembers ?? 0}
              bgColor={Colors.ChipColors.Red}
              memberStatus={MemberStatusTabKeysEnum.NotPaid}
              numberRef={registerNumberEl(1)}
              numberFontSizePx={syncedFontPx}
            />
            <ColoredBox
              label={t('dashboard:members.coloredBox.interested')}
              isLoading={isLoading}
              count={stats?.nrOfNewAppliedMembers ?? 0}
              bgColor={Colors.ChipColors.Yellow}
              memberStatus={MemberStatusTabKeysEnum.NewMember}
              numberRef={registerNumberEl(2)}
              numberFontSizePx={syncedFontPx}
            />
          </Grid>
        )}
      </Box>
    </Stack>
  );
};
